import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  GlobalStyles,
  Paper,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
import NewReleasesRoundedIcon from "@mui/icons-material/NewReleasesRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import BuildCircleRoundedIcon from "@mui/icons-material/BuildCircleRounded";
import BugReportRoundedIcon from "@mui/icons-material/BugReportRounded";
import StorageRoundedIcon from "@mui/icons-material/StorageRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { useRecoilValue } from "recoil";
import { LoginUserAtom } from "../stores/LoginUserAtom";

import axios from "axios";
import ConstList from "../utils/ConstList";
import { designTokens } from "../theme";

type ReleaseNoteRow = {
  ID: number | string;
  Version: string;
  ChangeDay: string;
  Title: string;
  Summary: string;
  Features: string[];
  Improvements: string[];
  Fixes: string[];
  DataApi: string[];
  Notices: string[];
};

const colors = {
  bg: designTokens.surface.page,
  panel: designTokens.surface.paper,
  panel2: designTokens.surface.subtle,
  accent: designTokens.brand.dark,
  accentButton: designTokens.brand.primary,
  accentSoft: designTokens.overlay.brandSoft,
  accentBorder: designTokens.overlay.brandMedium,
  text: designTokens.text.primary,
  textSoft: designTokens.text.secondary,
  textMuted: designTokens.text.muted,
  border: designTokens.border.default,
  danger: designTokens.state.error,
  dangerSoft: "rgba(220,90,82,0.08)",
  dangerBorder: "rgba(220,90,82,0.26)",
  info: designTokens.state.info,
  warning: designTokens.state.warning,
};

const toText = (value: unknown) => {
  if (value === null || value === undefined) return "";
  return String(value);
};

const toList = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.map((item) => toText(item).trim()).filter(Boolean);
  }

  return toText(value)
    .split(/\r?\n/)
    .map((item) => item.replace(/^[-・\s]+/, "").trim())
    .filter(Boolean);
};

const formatDate = (value: unknown) => {
  const text = toText(value);

  if (!text) return "";

  const date = new Date(text);

  if (Number.isNaN(date.getTime())) return text;

  return date.toLocaleDateString("ja-JP");
};

const categoryMeta: Record<
  string,
  { icon: React.ReactNode; color: string; background: string }
> = {
  機能追加: {
    icon: <AutoAwesomeRoundedIcon fontSize="small" />,
    color: colors.accent,
    background: colors.accentSoft,
  },
  改善: {
    icon: <BuildCircleRoundedIcon fontSize="small" />,
    color: colors.info,
    background: "rgba(50,134,184,0.08)",
  },
  修正: {
    icon: <BugReportRoundedIcon fontSize="small" />,
    color: colors.danger,
    background: colors.dangerSoft,
  },
  "データ / API": {
    icon: <StorageRoundedIcon fontSize="small" />,
    color: "#6B5FC7",
    background: "rgba(107,95,199,0.08)",
  },
  注意事項: {
    icon: <InfoOutlinedIcon fontSize="small" />,
    color: colors.warning,
    background: "rgba(217,142,4,0.08)",
  },
};

const CategoryList = ({ title, items }: { title: string; items: string[] }) => {
  if (!items || items.length === 0) return null;

  const meta = categoryMeta[title] ?? {
    icon: <InfoOutlinedIcon fontSize="small" />,
    color: colors.accent,
    background: colors.accentSoft,
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        borderRadius: 2,
        backgroundColor: meta.background,
        border: `1px solid ${colors.border}`,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Box
          sx={{
            width: 30,
            height: 30,
            display: "grid",
            placeItems: "center",
            borderRadius: 1.5,
            color: meta.color,
            backgroundColor: colors.panel,
            border: `1px solid ${colors.border}`,
            flexShrink: 0,
          }}
        >
          {meta.icon}
        </Box>

        <Typography
          sx={{
            color: meta.color,
            fontSize: 13,
            fontWeight: 900,
          }}
        >
          {title}
        </Typography>

        <Chip
          size="small"
          label={`${items.length}件`}
          sx={{
            height: 22,
            color: meta.color,
            backgroundColor: colors.panel,
            border: `1px solid ${colors.border}`,
            fontSize: 11,
            fontWeight: 800,
          }}
        />
      </Stack>

      <Stack spacing={0.8}>
        {items.map((item, index) => (
          <Stack
            key={index}
            direction="row"
            spacing={1}
            alignItems="flex-start"
          >
            <Box
              sx={{
                width: 5,
                height: 5,
                borderRadius: "50%",
                backgroundColor: meta.color,
                mt: 0.95,
                flexShrink: 0,
              }}
            />

            <Typography
              sx={{
                color: colors.textSoft,
                fontSize: 13,
                lineHeight: 1.7,
                whiteSpace: "pre-line",
                wordBreak: "break-word",
              }}
            >
              {item}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
};

const normalizeReleaseNotes = (rawList: any[]): ReleaseNoteRow[] => {
  return rawList.map((value: any, index: number) => {
    const version = toText(value.version ?? value.Version ?? index + 1);

    return {
      ID: value.release_note_id ?? value.version_id ?? value.ID ?? index,
      Version: version,

      ChangeDay: formatDate(
        value.release_date ?? value.changes_date ?? value.ChangeDay,
      ),

      Title: toText(value.title) || `Ver.${version}`,
      Summary:
        toText(value.summary) ||
        toText(value.description) ||
        "営業管理システムの変更内容です。",

      Features: [
        ...toList(value.features),
        ...toList(value.new_features),
        ...toList(value.Change),
      ],

      Improvements: [
        ...toList(value.improvements),
        ...toList(value.improvement),
        ...toList(value.changes),
      ],

      Fixes: [
        ...toList(value.fixes),
        ...toList(value.bug_fixes),
        ...toList(value.category),
        ...toList(value.Contents),
      ],

      DataApi: [
        ...toList(value.data_api),
        ...toList(value.dataApi),
        ...toList(value.api_changes),
        ...toList(value.database_changes),
      ],

      Notices: [
        ...toList(value.notices),
        ...toList(value.notes),
        ...toList(value.attention),
      ],
    };
  });
};

const ReleaseNotes: React.FC = () => {
  const loginUserData = useRecoilValue(LoginUserAtom);

  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [releaseNoteData, setReleaseNoteData] = useState<ReleaseNoteRow[]>([]);

  const fetchData = async () => {
    setLoading(true);
    setErrorMessage(null);

    const postData = {
      flag: "GetSalesReleaseNotes",
      access_token: loginUserData.accessToken,
      algorithm: loginUserData.algorithm,
    };

    try {
      const response = await axios.post(
        ConstList.getApiUrl() + "SalesReleaseNotes.php",
        postData,
        ConstList.AXIOS_CONFIG,
      );

      if (response.data?.data === false) {
        setErrorMessage(
          response.data?.message ?? "リリースノートの取得に失敗しました。",
        );

        if (response.data?.reDirect) {
          await ConstList.SLEEP(5000);
          window.location.href = response.data.reDirect;
        }

        return;
      }

      const payload = response.data?.data ?? {};

      const rawList =
        payload.release_notes ??
        payload.releaseNotes ??
        payload.sales_release_notes ??
        payload.ReleaseData ??
        payload.ReleaceData ??
        [];

      setReleaseNoteData(
        normalizeReleaseNotes(Array.isArray(rawList) ? rawList : []),
      );
    } catch (error) {
      console.error("リリースノート取得エラー:", error);
      setErrorMessage("リリースノートの取得に失敗しました。");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!loginUserData?.accessToken || !loginUserData?.algorithm) return;
    fetchData();
  }, [loginUserData?.accessToken, loginUserData?.algorithm]);

  return (
    <>
      <GlobalStyles
        styles={{
          html: { overflowY: "auto" },
          body: { overflowY: "auto" },
          "#root": { overflowY: "auto" },
        }}
      />

      <Toolbar />

      <Box
        sx={{
          minHeight: "calc(100dvh - 64px)",
          overflowY: "visible",
          overflowX: "hidden",
          background:
            "radial-gradient(circle at top right, rgba(152,188,46,0.07), transparent 30%), #F4F6F8",
          color: colors.text,
          p: { xs: 1.25, md: 2 },
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 1100,
            mx: "auto",
            height: "100%",
            minHeight: 0,
            display: "grid",
            gridTemplateRows: "auto minmax(0, 1fr)",
            gap: 1.5,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.6, md: 2 },
              borderRadius: 3,
              backgroundColor: colors.panel,
              border: `1px solid ${colors.border}`,
              boxShadow: "0 8px 24px rgba(15,23,42,0.06)",
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              spacing={1.5}
            >
              <Stack direction="row" spacing={1.3} alignItems="center">
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    display: "grid",
                    placeItems: "center",
                    borderRadius: 2,
                    color: colors.accent,
                    backgroundColor: colors.accentSoft,
                    border: `1px solid ${colors.accentBorder}`,
                    flexShrink: 0,
                  }}
                >
                  <CampaignRoundedIcon />
                </Box>

                <Box>
                  <Typography
                    sx={{
                      color: colors.text,
                      fontSize: { xs: 20, md: 24 },
                      fontWeight: 900,
                      letterSpacing: "0.02em",
                    }}
                  >
                    リリースノート
                  </Typography>

                  <Typography
                    sx={{
                      color: colors.textMuted,
                      fontSize: 0,
                      mt: 0.25,
                      "&::after": {
                        content: '"営業管理システムの変更内容と改善履歴を確認できます。"',
                        display: "block",
                        fontSize: 13,
                      },
                    }}
                  >
                    営業管理システムの変更内容と改善履歴を確認できます。
                  </Typography>
                </Box>
              </Stack>

              <Stack direction="row" spacing={1}>
                <Chip
                  icon={<NewReleasesRoundedIcon />}
                  label={`${releaseNoteData.length} バージョン`}
                  sx={{
                    color: colors.accent,
                    backgroundColor: colors.accentSoft,
                    border: `1px solid ${colors.accentBorder}`,
                    fontWeight: 800,
                    "& .MuiChip-icon": { color: colors.accent },
                  }}
                />
              </Stack>
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              minHeight: 0,
              overflow: "hidden",
              borderRadius: 3,
              backgroundColor: colors.panel,
              border: `1px solid ${colors.border}`,
              boxShadow: "0 8px 24px rgba(15,23,42,0.06)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {errorMessage && (
              <Alert
                severity="error"
                sx={{
                  m: 1.5,
                  mb: 0,
                  backgroundColor: colors.dangerSoft,
                  color: colors.text,
                  border: `1px solid ${colors.dangerBorder}`,
                  "& .MuiAlert-icon": {
                    color: colors.danger,
                  },
                }}
              >
                {errorMessage}
              </Alert>
            )}

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                p: { xs: 1.25, md: 1.5 },
              }}
            >
              {loading ? (
                <Box
                  sx={{
                    minHeight: 260,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: colors.textMuted,
                  }}
                >
                  <CircularProgress sx={{ color: colors.accent }} />
                  <Typography sx={{ ml: 1.5 }}>読み込み中...</Typography>
                </Box>
              ) : releaseNoteData.length === 0 ? (
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  spacing={1.2}
                  sx={{
                    minHeight: 300,
                    p: 4,
                    textAlign: "center",
                    backgroundColor: colors.panel2,
                    border: `1px dashed ${colors.border}`,
                    borderRadius: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      display: "grid",
                      placeItems: "center",
                      borderRadius: 3,
                      color: colors.accent,
                      backgroundColor: colors.accentSoft,
                      border: `1px solid ${colors.accentBorder}`,
                    }}
                  >
                    <CampaignRoundedIcon sx={{ fontSize: 34 }} />
                  </Box>
                  <Typography fontWeight={900}>
                    表示できるリリースノートがありません
                  </Typography>
                  <Typography sx={{ color: colors.textMuted, fontSize: 13 }}>
                    更新情報が登録されると、ここにバージョン別で表示されます。
                  </Typography>
                </Stack>
              ) : (
                <Stack spacing={1.2}>
                  {releaseNoteData.map((note, index) => {
                    const categoryCount = [
                      note.Features,
                      note.Improvements,
                      note.Fixes,
                      note.DataApi,
                      note.Notices,
                    ].filter((items) => items.length > 0).length;

                    return (
                      <Accordion
                        key={`${note.ID}-${note.Version}`}
                        disableGutters
                        sx={{
                          backgroundColor: colors.panel,
                          color: colors.text,
                          border: `1px solid ${
                            index === 0 ? colors.accentBorder : colors.border
                          }`,
                          borderRadius: "14px !important",
                          overflow: "hidden",
                          boxShadow:
                            index === 0
                              ? "0 10px 26px rgba(15,23,42,0.07)"
                              : "0 4px 14px rgba(15,23,42,0.04)",
                          "&::before": {
                            display: "none",
                          },
                        }}
                      >
                        <AccordionSummary
                          expandIcon={
                            <ExpandMoreIcon sx={{ color: colors.textMuted }} />
                          }
                          sx={{
                            minHeight: 72,
                            px: { xs: 1.5, md: 2 },
                            backgroundColor:
                              index === 0 ? colors.accentSoft : colors.panel,
                            "& .MuiAccordionSummary-content": {
                              my: 1.1,
                            },
                          }}
                        >
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1.2}
                            alignItems={{ xs: "flex-start", sm: "center" }}
                            sx={{ width: "100%", minWidth: 0 }}
                          >
                            <Box
                              sx={{
                                minWidth: 82,
                                px: 1.1,
                                py: 0.7,
                                borderRadius: 2,
                                textAlign: "center",
                                color: "#17210B",
                                backgroundColor: colors.accentButton,
                                border: `1px solid ${colors.accentBorder}`,
                                fontWeight: 900,
                                fontSize: 13,
                              }}
                            >
                              Ver.{note.Version}
                            </Box>

                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Stack
                                direction="row"
                                spacing={0.8}
                                alignItems="center"
                                flexWrap="wrap"
                                useFlexGap
                              >
                                <Typography
                                  sx={{
                                    color: colors.text,
                                    fontWeight: 900,
                                    fontSize: 15,
                                  }}
                                >
                                  {note.Title}
                                </Typography>

                                {index === 0 && (
                                  <Chip
                                    size="small"
                                    label="最新"
                                    sx={{
                                      height: 22,
                                      color: colors.accent,
                                      backgroundColor: colors.panel,
                                      border: `1px solid ${colors.accentBorder}`,
                                      fontWeight: 900,
                                      fontSize: 11,
                                    }}
                                  />
                                )}
                              </Stack>

                              <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={{ xs: 0.3, sm: 1.2 }}
                                sx={{ mt: 0.45 }}
                              >
                                <Stack
                                  direction="row"
                                  spacing={0.5}
                                  alignItems="center"
                                >
                                  <CalendarMonthRoundedIcon
                                    sx={{
                                      fontSize: 15,
                                      color: colors.textMuted,
                                    }}
                                  />
                                  <Typography
                                    sx={{
                                      color: colors.textMuted,
                                      fontSize: 12.5,
                                    }}
                                  >
                                    {note.ChangeDay || "-"}
                                  </Typography>
                                </Stack>

                                <Typography
                                  sx={{
                                    color: colors.textSoft,
                                    fontSize: 12.5,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: { xs: "normal", sm: "nowrap" },
                                  }}
                                >
                                  {note.Summary}
                                </Typography>
                              </Stack>
                            </Box>

                            <Chip
                              size="small"
                              label={`${categoryCount}カテゴリ`}
                              sx={{
                                color: colors.textMuted,
                                backgroundColor: colors.panel2,
                                border: `1px solid ${colors.border}`,
                                fontWeight: 700,
                                flexShrink: 0,
                              }}
                            />
                          </Stack>
                        </AccordionSummary>

                        <AccordionDetails
                          sx={{
                            p: { xs: 1.25, md: 1.5 },
                            backgroundColor: colors.panel,
                            borderTop: `1px solid ${colors.border}`,
                          }}
                        >
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: {
                                xs: "1fr",
                                md: "repeat(2, minmax(0, 1fr))",
                              },
                              gap: 1,
                            }}
                          >
                            <CategoryList
                              title="機能追加"
                              items={note.Features}
                            />
                            <CategoryList
                              title="改善"
                              items={note.Improvements}
                            />
                            <CategoryList title="修正" items={note.Fixes} />
                            <CategoryList
                              title="データ / API"
                              items={note.DataApi}
                            />
                            <CategoryList
                              title="注意事項"
                              items={note.Notices}
                            />
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </Stack>
              )}
            </Box>
          </Paper>
        </Box>
      </Box>
    </>
  );
};

export default ReleaseNotes;
